import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent } from 'react';
import {
  MAX_FEEDBACK_IMAGES,
  collectSubmitAttachments,
  validateFeedbackImages,
  type CapturedScreenshot,
  type ImageRejection,
  type PendingAttachment,
  type SubmitAttachment,
} from './feedbackAttachmentRules';
import { captureViewportScreenshot } from './captureViewportScreenshot';

/** Object URLs for previews, revoked on removal and on unmount. */
function useObjectUrlRegistry() {
  const liveUrls = useRef(new Set<string>());
  function track(blob: Blob) {
    const url = URL.createObjectURL(blob);
    liveUrls.current.add(url);
    return url;
  }
  function release(url: string) {
    if (liveUrls.current.delete(url)) URL.revokeObjectURL(url);
  }
  useEffect(() => {
    const urls = liveUrls.current;
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);
  return { track, release };
}

/**
 * Everything the panel attaches: page screenshots captured on demand (as
 * many as the user wants, from any page) and the user's own image files.
 * Files stay in memory until submit; nothing is uploaded from here. Object
 * URLs are revoked when an item is removed and on unmount.
 */
export function useFeedbackAttachments() {
  const [screenshots, setScreenshots] = useState<CapturedScreenshot[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [files, setFiles] = useState<PendingAttachment[]>([]);
  const nextId = useRef(0);
  // Set synchronously so two calls in the same tick (React StrictMode runs
  // mount effects twice in dev) produce one screenshot, not two.
  const captureInFlight = useRef(false);
  const { track, release } = useObjectUrlRegistry();

  // A capture in flight holds a slot, so files added meanwhile cannot push
  // the final list past the cap.
  const total = screenshots.length + files.length + (isCapturing ? 1 : 0);

  function appendScreenshot(blob: Blob) {
    const shot: CapturedScreenshot = {
      id: `shot-${nextId.current++}`,
      blob,
      previewUrl: track(blob),
      pageUrl: window.location.href,
      pageTitle: document.title,
    };
    setScreenshots((prev) => [...prev, shot]);
  }

  /**
   * Screenshot the page behind the panel and append it. Deferred two frames
   * so any pending render (the panel opening, a minimize) has painted first.
   */
  function capturePage() {
    if (captureInFlight.current || total >= MAX_FEEDBACK_IMAGES) return;
    captureInFlight.current = true;
    setIsCapturing(true);
    setCaptureError(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void captureViewportScreenshot().then((result) => {
          captureInFlight.current = false;
          setIsCapturing(false);
          if (result.ok) appendScreenshot(result.blob);
          else setCaptureError(result.error);
        });
      });
    });
  }

  function removeScreenshot(id: string) {
    const target = screenshots.find((s) => s.id === id);
    if (target) release(target.previewUrl);
    setScreenshots((prev) => prev.filter((s) => s.id !== id));
  }

  function addFiles(incoming: File[]): ImageRejection[] {
    const { accepted, rejected } = validateFeedbackImages(incoming, total);
    if (accepted.length > 0) {
      const added = accepted.map((file) => ({
        id: `att-${nextId.current++}`,
        file,
        previewUrl: track(file),
      }));
      setFiles((prev) => [...prev, ...added]);
    }
    return rejected;
  }

  function removeFile(id: string) {
    const target = files.find((f) => f.id === id);
    if (target) release(target.previewUrl);
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  /** Pasted images become attachments; other pastes fall through. */
  function onPaste(event: ClipboardEvent<HTMLElement>): ImageRejection[] {
    const pasted = Array.from(event.clipboardData?.files ?? []);
    if (pasted.length === 0) return [];
    event.preventDefault();
    return addFiles(pasted);
  }

  function collect(now: Date): SubmitAttachment[] {
    return collectSubmitAttachments(screenshots, files, now);
  }

  return {
    screenshots,
    files,
    isCapturing,
    captureError,
    remaining: MAX_FEEDBACK_IMAGES - total,
    capturePage,
    removeScreenshot,
    addFiles,
    removeFile,
    onPaste,
    collect,
  };
}
